'use client';

import { memo, useCallback, useMemo } from 'react';

import { ArrowDownIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import {
  getListViewColumn,
  getListViewGridTemplateColumns,
  getToggledListViewSort,
  LIST_VIEW_NAME_SORT_COLUMN,
  type ListViewColumn,
  type ListViewColumnField,
  type ListViewSort,
} from '~/constants/listViewColumns';

export type { ListViewSort };

export type ListViewHeaderProps = {
  onSortChange: (sort: ListViewSort) => void;
  sort: ListViewSort;
  visibleColumns: ListViewColumnField[];
};

export type SortableHeaderCellProps = {
  column: ListViewColumn;
  headerClassName?: string;
  isActive: boolean;
  onSortChange: (sort: ListViewSort) => void;
  sort: ListViewSort;
  testId?: string;
};

const SortableHeaderCell = memo(
  ({ column, headerClassName, isActive, onSortChange, sort, testId }: SortableHeaderCellProps) => {
    const handleClick = useCallback(() => {
      if (!column.sortable) {
        return;
      }

      onSortChange(getToggledListViewSort(sort, column.sortFieldName));
    }, [column.sortFieldName, column.sortable, onSortChange, sort]);

    if (!column.sortable) {
      return (
        <div
          className={cn(
            'flex items-center px-1.5 py-1.5 font-semibold text-10 text-neutral-10 uppercase tracking-wide',
            headerClassName,
          )}
        >
          <span className="truncate">{column.label}</span>
        </div>
      );
    }

    return (
      <button
        className={cn(
          'group flex w-full min-w-0 items-center gap-1 px-1.5 py-1.5 text-left font-semibold text-10 uppercase tracking-wide transition-colors',
          isActive ? 'text-neutral-12' : 'text-neutral-10 hover:text-neutral-12',
          headerClassName,
        )}
        // biome-ignore lint/plugin/no-compound-data-testid: pre-existing — Cypress specs query this interpolated value directly; renaming requires updating those specs
        data-testid={testId ?? `list-view-sort-${column.field}`}
        onClick={handleClick}
        type="button"
      >
        <span className="truncate">{column.label}</span>
        <ArrowDownIcon
          aria-hidden="true"
          className={cn(
            'shrink-0 transition-all',
            sort.direction === 'asc' ? 'rotate-180' : 'rotate-0',
            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          )}
          size="xs"
        />
      </button>
    );
  },
);

SortableHeaderCell.displayName = 'SortableHeaderCell';

export const ListViewHeader = memo(({ onSortChange, sort, visibleColumns }: ListViewHeaderProps) => {
  const gridTemplateColumns = useMemo(() => getListViewGridTemplateColumns(visibleColumns), [visibleColumns]);

  return (
    <div
      className="sticky top-0 z-10 grid w-full min-w-0 border-neutral-4 border-b bg-neutral-1"
      data-testid="list-view-header"
      style={{ gridTemplateColumns }}
    >
      <SortableHeaderCell
        column={LIST_VIEW_NAME_SORT_COLUMN}
        headerClassName="px-2"
        isActive={sort.name === 'name'}
        onSortChange={onSortChange}
        sort={sort}
        testId="list-view-sort-name"
      />

      {visibleColumns.map((field) => {
        const column = getListViewColumn(field);

        return (
          <SortableHeaderCell
            column={column}
            isActive={sort.name === column.sortFieldName}
            key={field}
            onSortChange={onSortChange}
            sort={sort}
          />
        );
      })}
    </div>
  );
});

ListViewHeader.displayName = 'ListViewHeader';
